import { useEffect, useRef } from 'preact/hooks'
import { Text, useText } from 'preact-i18n'
import { useRegisterSW } from 'virtual:pwa-register/preact'
import css from './index.module.css'

// How often to ping the server for an updated service worker while the tab
// stays open. 5 min balances responsiveness against extra /sw.js HEAD
// requests; combined with the visibilitychange listener below, a user who
// switches back to the tab usually sees the prompt within seconds of any
// new deploy.
const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000

export const UpdateAvailableNotification = () => {
  // The registration is captured in a ref so the polling effect can read it
  // without re-subscribing each time useRegisterSW returns a fresh object.
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null)

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration ?? null
    },
  })

  // useText must be called unconditionally so hook order is stable.
  const { dismissAriaLabel } = useText({ dismissAriaLabel: 'pwa.dismissAriaLabel' })

  useEffect(() => {
    const triggerUpdate = () => {
      const reg = registrationRef.current
      if (reg) void reg.update()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') triggerUpdate()
    }
    const intervalId = setInterval(triggerUpdate, UPDATE_CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  if (!needRefresh) return null

  const handleReload = () => {
    void updateServiceWorker()
  }

  const handleDismiss = () => {
    setNeedRefresh(false)
  }

  return (
    <div className={css.container} role="status" aria-live="polite">
      <div className={css.banner}>
        <span className={css.message}>
          <Text id="pwa.updateAvailable" />
        </span>
        <div className={css.actions}>
          <button type="button" onClick={handleReload}>
            <Text id="actions.reload" />
          </button>
          <button type="button" onClick={handleDismiss} aria-label={dismissAriaLabel}>
            <Text id="actions.dismiss" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default UpdateAvailableNotification
